// pages/public/TicketForm.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Send, 
  CheckCircle, 
  AlertCircle, 
  X,
  Phone,
  Mail,
  User,
  Tag,
  MessageSquare,
  Paperclip,
  Loader2
} from 'lucide-react';
import { entrepriseService } from '../../services/entrepriseService';
import { Entreprise } from '../../types/features/Entreprise';
import { problemCategoryService } from '../../services/problemCategoryService';
import { uploadService } from '../../services/uploadService';
import { ticketService } from '../../services/ticketService';
import { ProblemCategory } from '../../types/features/ProblemCategory';

interface TicketFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  subject: string;
  description: string;
  priority: 'basse' | 'moyenne' | 'haute' | 'critique';
  category: string;
  attachments: File[];
}

export const PublicTicketFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [company, setCompany] = useState<Entreprise | null>(null);
  const [categories, setCategories] = useState<ProblemCategory[]>([]);
  const [isLoadingCompany, setIsLoadingCompany] = useState(true);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [companyError, setCompanyError] = useState<string | null>(null);
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ticketNumber, setTicketNumber] = useState<any | null>(null);
  
  const [formData, setFormData] = useState<TicketFormData>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    subject: '',
    description: '',
    priority: 'moyenne',
    category: '',
    attachments: []
  });

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        setIsLoadingCompany(true);
        const entreprise = await entrepriseService.getPublicEntrepriseById(id!);
        setCompany(entreprise);
      } catch (err) {
        setCompanyError('Entreprise non trouvee');
      } finally {
        setIsLoadingCompany(false);
      }
    };

    const fetchCategories = async () => {
      try {
        setIsLoadingCategories(true);
        const data = await problemCategoryService.getPublicCategories(id!);
        setCategories(data || []);
      } catch (err) {
        console.error('Erreur chargement categories:', err);
        setCategories([]);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    if (id) {
      fetchCompany();
      fetchCategories();
    }
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      
      // Vérifier la taille totale (max 10 Mo)
      const totalSize = [...formData.attachments, ...newFiles].reduce((acc, file) => acc + file.size, 0);
      if (totalSize > 10 * 1024 * 1024) {
        setError('La taille totale des fichiers ne doit pas depasser 10 Mo');
        return;
      }

      // Vérifier chaque fichier
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const invalidFiles = newFiles.filter(file => !allowedTypes.includes(file.type));
      if (invalidFiles.length > 0) {
        setError('Certains fichiers ne sont pas supportes. Utilisez JPG, PNG, PDF ou DOC');
        return;
      }

      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...newFiles]
      }));
      setError(null);
    }
  };

  const removeAttachment = (index: number) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // 1. Upload des pièces jointes
      let attachmentUrls: string[] = [];
      
      if (formData.attachments.length > 0) {
        const uploadResult = await uploadService.uploadMultiple({
          files: formData.attachments,
          user_id: id!,
          type: 'image',
          reason: 'Tickets'
        });
        attachmentUrls = uploadResult.map((u)=>(
          u.url
        )) || [];
      }

      // 2. Création du ticket
      const payload = {
        entreprise_id: id!,
        contact: {
          email: formData.email,
          name: `${formData.first_name} ${formData.last_name}`,
          phone: formData.phone || undefined
        },
        subject: formData.subject,
        description: formData.description,
        priority: formData.priority,
        category: formData.category,
        attachments: attachmentUrls
      };

      const response = await ticketService.createTicketByPublicForm(payload);
       
  
      if (!response.success) {
        throw new Error('Une erreur est survenue');
      }

      const ticket = response.data


      setTicketNumber(ticket?.position || 0);
      setIsSuccess(true);

      navigate(`/ticket?ref=${ticket.ticket_number}`)
      
      // Reset form
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        subject: '',
        description: '',
        priority: 'moyenne',
        category: '',
        attachments: []
      });

    } catch (err: any) {
      console.log(err.response)
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingCompany || isLoadingCategories) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Chargement du formulaire...</p>
        </div>
      </div>
    );
  }

  if (companyError || !company) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Formulaire indisponible</h2>
          <p className="text-gray-600">{companyError || 'Cette entreprise n\'existe pas ou a desactive le formulaire.'}</p>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-2xl w-full mx-auto p-6">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={40} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Ticket envoye avec succes !
            </h2>
            <p className="text-gray-600 mb-2">
              Votre demande a bien ete prise en compte.
            </p>
            {ticketNumber && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4 inline-block">
                <p className="text-sm text-gray-500">Numero de suivi</p>
                <p className="text-xl font-mono font-bold text-primary">{ticketNumber}</p>
              </div>
            )}
            <p className="text-sm text-gray-500">
              Vous recevrez un email de confirmation dans quelques instants.
            </p>
            <button
              onClick={() => setIsSuccess(false)}
              className="mt-6 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              Creer un autre ticket
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          {company.logo && (
            <img 
              src={company.logo} 
              alt={company.name} 
              className="h-16 mx-auto mb-4 object-contain"
            />
          )}
          <h1 className="text-3xl font-bold text-gray-900">
            {company.name}
          </h1>
          <p className="text-gray-600 mt-2">
            Une question ? Un probleme ? Remplissez ce formulaire et notre equipe vous repondra rapidement.
          </p>
          {company?.twilio_phone_numbers?.[0] && (
            <p className="text-sm text-gray-500 mt-2">
              {company?.twilio_phone_numbers?.[0]}
            </p>
          )}
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Prenom <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Jean"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Nom <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Dupont"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="jean.dupont@email.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Telephone
              </label>
              <div className="relative">
                <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="+33 6 12 34 56 78"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Categorie <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Tag size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent appearance-none bg-white"
                >
                  <option value="">Selectionner une categorie</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.slug || ""}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Sujet <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Ex: Je n'arrive pas a me connecter"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Description <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <MessageSquare size={18} className="absolute left-3 top-3 text-gray-400" />
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  placeholder="Decrivez votre probleme en detail..."
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Priorite
              </label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent appearance-none bg-white"
              >
                <option value="basse">Basse</option>
                <option value="moyenne">Moyenne</option>
                <option value="haute">Haute</option>
                <option value="critique">Critique</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Pieces jointes (optionnel)
              </label>
              <div className="relative">
                <input
                  type="file"
                  id="attachments"
                  onChange={handleFileChange}
                  className="hidden"
                  multiple
                  accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                />
                <label
                  htmlFor="attachments"
                  className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary transition-colors"
                >
                  <Paperclip size={18} className="text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600">
                    {formData.attachments.length > 0 
                      ? `${formData.attachments.length} fichier(s) selectionne(s)` 
                      : 'Cliquez pour joindre des fichiers (JPG, PNG, PDF, DOC - max 10Mo)'}
                  </span>
                </label>
              </div>
              
              {formData.attachments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {formData.attachments.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2">
                      <span className="text-sm text-gray-600 truncate">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="p-1 hover:bg-gray-200 rounded-full"
                      >
                        <X size={16} className="text-gray-500" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full py-3 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Envoyer mon ticket
                </>
              )}
            </button>

            <p className="text-xs text-gray-500 text-center">
              En soumettant ce formulaire, vous acceptez que vos donnees soient traitees pour le suivi de votre demande.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};